import { t } from '@lingui/macro'
import { RequestDTO } from '@om1/aspen-api/codegen/client/models/RequestDTO'
import { useRequests } from '@om1/aspen-api/codegen/hooks/useRequests'
import { formatDateTime } from '@om1/platform-components'
import { AlertCircle, Check, Clock, X } from 'lucide-react'
import React from 'react'
import { Column, GenericDataPage } from './GenericDataPage'

// Status badge component for better visual representation
const StatusBadge = ({ status }: { status: string }) => {
    let bgColor = 'bg-gray-100'
    let textColor = 'text-gray-700'
    let icon: React.ReactNode | null = null
    let description = ''

    // Map status to appropriate colors, icons, and descriptions
    if (status === 'OM1_NEW') {
        bgColor = 'bg-blue-100'
        textColor = 'text-blue-800'
        icon = <Clock size={16} className='mr-1' />
        description = t`Patient newly added to Greenlight`
    } else if (status === 'CANCELLED') {
        bgColor = 'bg-red-100'
        textColor = 'text-red-800'
        icon = <X size={16} className='mr-1' />
        description = t`Request cancelled`
    } else if (status === 'CLOSED') {
        bgColor = 'bg-red-100'
        textColor = 'text-red-800'
        icon = <X size={16} className='mr-1' />
        description = t`Request closed, data still accessible`
    } else if (status === 'CRED_FAILED') {
        bgColor = 'bg-red-100'
        textColor = 'text-red-800'
        icon = <X size={16} className='mr-1' />
        description = t`Login error with user credentials`
    } else if (status === 'DOB_CHECK_FAILED') {
        bgColor = 'bg-red-100'
        textColor = 'text-red-800'
        icon = <X size={16} className='mr-1' />
        description = t`DOB mismatch; re-enable connection`
    } else if (status === 'DATA_RETR') {
        bgColor = 'bg-green-100'
        textColor = 'text-green-800'
        icon = <Check size={16} className='mr-1' />
        description = t`New data may be available`
    } else if (status === 'CRED_SKIPPED') {
        bgColor = 'bg-yellow-100'
        textColor = 'text-yellow-800'
        icon = <AlertCircle size={16} className='mr-1' />
        description = t`User skipped credential entry`
    } else if (status === 'FHIR_TOKEN_EXPIRED') {
        bgColor = 'bg-yellow-100'
        textColor = 'text-yellow-800'
        icon = <AlertCircle size={16} className='mr-1' />
        description = t`User token expired`
    } else if (status === 'DATA_UNAV_2FA') {
        bgColor = 'bg-yellow-100'
        textColor = 'text-yellow-800'
        icon = <AlertCircle size={16} className='mr-1' />
        description = t`Data unavailable - 2FA enabled on portal`
    } else if (status === 'DATA_UNAV_USER_INTERACTION_NEEDED') {
        bgColor = 'bg-yellow-100'
        textColor = 'text-yellow-800'
        icon = <AlertCircle size={16} className='mr-1' />
        description = t`Data unavailable - user action needed in portal`
    } else {
        icon = <AlertCircle size={16} className='mr-1' />
    }

    const displayStatus = status.replace(/_/g, ' ').toLowerCase()

    return (
        <div
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${bgColor} ${textColor} text-sm font-medium shadow-sm`}
            title={description || displayStatus}
        >
            {icon}
            <span className='capitalize'>{displayStatus}</span>
        </div>
    )
}

const requestColumns: Column<RequestDTO>[] = [
    { key: 'greenlightId', type: 'text' },
    { key: 'patient.firstName', type: 'text' },
    { key: 'patient.lastName', type: 'text' },
    { key: 'provider.name', type: 'text' },
    { key: 'provider.zipCode', type: 'text' },
    // Remove the status column from the regular columns as we'll add it as a custom column
    {
        key: 'changesNextStartingTimestamp',
        type: 'date',
        displayValue: (value: string) => {
            if (!value) return 'N/A'
            return formatDateTime(value)
        }
    }
]

const RequestsPage: React.FC = () => {
    return (
        <GenericDataPage<RequestDTO, 'id'>
            // @ts-ignore
            useData={useRequests}
            useDataArgs={[]}
            columns={requestColumns as Column<RequestDTO>[]}
            title={t`Requests`}
            customColumns={[
                {
                    label: t`Status`,
                    value: (item) => <StatusBadge status={item.status} />
                }
            ]}
        />
    )
}

export default RequestsPage
